import { createSelector } from '@reduxjs/toolkit';
import { resourceSelectors } from '../../utils/resourceSelectors';

export const [
  portfolioCashflowAllIds,
  portfolioCashflowById,
  allPortfolioCashflowSelector,
  portfolioCashflowMeta,
] = resourceSelectors('portfolioCashflow');

export const allPortfolioCashflowStrategiesSelector = createSelector(
  portfolioCashflowMeta,
  (meta) => {
    if (meta && meta.hasOwnProperty('strategies')) {
      return meta.strategies;
    }
    return [];
  }
);

const sortProducts = (products) =>
  products.sort(
    (a, b) =>
      Number(
        (a.symbol?.includes('RE_CORE+') || a.name?.includes('CORE +')) -
          (b.symbol?.includes('RE_CORE+') || b.name?.includes('CORE +'))
      ) ||
      (Number(a.securityId) || 0) - (Number(b.securityId) || 0) ||
      (a.name || a.symbol || '').localeCompare(b.name || b.symbol || '')
  );

export const portfolioCashflowProductsSelector = createSelector(
  portfolioCashflowMeta,
  (meta) => {
    if (meta && meta.hasOwnProperty('products')) {
      return {
        realEstate: sortProducts([...(meta.products.realEstate || [])]),
        privateEquity: sortProducts([...(meta.products.privateEquity || [])]),
      };
    }
    return { realEstate: [], privateEquity: [] };
  }
);

export const currentPortfolioCashflowSelector = createSelector(
  allPortfolioCashflowSelector,
  portfolioCashflowMeta,
  (items, meta) => {
    if (!items || !Array.isArray(items) || !meta?.portfolioId) {
      return [];
    }

    // Only include items that match the current portfolio ID formatted as "portfolioId-year" (e.g. '847-2025')
    const portfolioIdPrefix = `${meta.portfolioId}-`;
    return items.filter(
      (item) => item.id && item.id.startsWith(portfolioIdPrefix)
    );
  }
);

export const createPortfolioCashflowByAssetClassSelector = (assetClass) => {
  return createSelector(currentPortfolioCashflowSelector, (items) => {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    return items.map((item) => {
      const assetData =
        assetClass === 'p'
          ? item.privateEquity
          : assetClass === 'i'
            ? item.realEstate
            : null;

      if (!assetData) {
        return item;
      }

      return {
        year: item.year,
        ...assetData,
      };
    });
  });
};

export const yearlyCashFlowSummaryByAssetClassSelector = createSelector(
  currentPortfolioCashflowSelector,
  (items) => {
    if (!items || !Array.isArray(items)) {
      return { realEstate: {}, privateEquity: {} };
    }

    const realEstateSummary = {};
    const privateEquitySummary = {};

    items.forEach((yearData) => {
      const { year, realEstate, privateEquity } = yearData;

      if (realEstate?.productCashFlows) {
        let sumToPayIn = 0;
        let sumDistributions = 0;

        realEstate.productCashFlows.forEach((product) => {
          const value = product.expectedCashFlow?.value;
          if (value !== null && value !== undefined) {
            if (value < 0) {
              sumToPayIn += value;
            } else if (value > 0) {
              sumDistributions += value;
            }
          }
        });

        realEstateSummary[year] = {
          sumToPayIn,
          sumDistributions,
          netFlow: sumToPayIn + sumDistributions,
        };
      }

      if (privateEquity?.productCashFlows) {
        let sumToPayIn = 0;
        let sumDistributions = 0;

        privateEquity.productCashFlows.forEach((product) => {
          const value = product.expectedCashFlow?.value;
          if (value !== null && value !== undefined) {
            if (value < 0) {
              sumToPayIn += value;
            } else if (value > 0) {
              sumDistributions += value;
            }
          }
        });

        privateEquitySummary[year] = {
          sumToPayIn,
          sumDistributions,
          netFlow: sumToPayIn + sumDistributions,
        };
      }
    });

    return {
      realEstate: realEstateSummary,
      privateEquity: privateEquitySummary,
    };
  }
);

// Merge external amounts from illiquid cashflow into portfolio cashflow data by year
export const mergeExternalData = (portfolioData, illiquidData) => {
  if (!portfolioData || portfolioData.length === 0) {
    return illiquidData || [];
  }

  const externalByYear = {};
  if (illiquidData && illiquidData.length > 0) {
    illiquidData.forEach((item) => {
      externalByYear[item.year] = item.externalAmount || 0;
    });
  }

  return portfolioData.map((item) => ({
    ...item,
    externalAmount: externalByYear[item.year] || 0,
  }));
};

// Replicate sumMarketValues from backoffice chartData.tsx
// Calculates "existing" market values (products + core allocations, no new commitments)
export const sumMarketValues = (data, assetClassCode, products, strategies) => {
  if (!data || data.length === 0) {
    return [];
  }

  // Get products from portfolio meta (portfolio cashflow) or fall back to illiquid strategy data
  let productList = [];

  if (
    products &&
    (products.privateEquity?.length > 0 || products.realEstate?.length > 0)
  ) {
    productList =
      assetClassCode === 'p' ? products.privateEquity : products.realEstate;
  } else {
    const strategy = strategies?.find(
      (s) =>
        s.assetClassCode === assetClassCode || s.assetClass === assetClassCode
    );
    productList = strategy?.products || [];

    if (productList.length > 0 && productList[0].expectedCashFlowValues) {
      const sumInitialMarketValue = productList.reduce(
        (acc, product) =>
          acc +
          ((product.marketValue ?? 0) +
            (product.ordersInProcessAmount ?? 0) +
            (product.otherValue ?? 0)),
        0
      );

      const yearlyExpectedCashFlow = [];
      for (let i = 0; i < productList.length; i++) {
        for (let j = 0; j < productList[i].expectedCashFlowValues.length; j++) {
          yearlyExpectedCashFlow[j] =
            (yearlyExpectedCashFlow[j] ?? 0) +
            productList[i].expectedCashFlowValues[j].value;
        }
      }

      const marketValuesByYear = [];
      let aggregatedExpectedMarketValue = sumInitialMarketValue;

      for (let i = 0; i < data.length; i++) {
        if (i < yearlyExpectedCashFlow.length) {
          marketValuesByYear.push(aggregatedExpectedMarketValue);
          aggregatedExpectedMarketValue -= yearlyExpectedCashFlow[i];
        } else {
          marketValuesByYear.push(0);
        }
      }

      data.forEach((item, index) => {
        const coreAllocation = item.coreProductAllocation;
        if (coreAllocation?.selected) {
          for (let i = index; i < marketValuesByYear.length; i++) {
            marketValuesByYear[i] += coreAllocation.value ?? 0;
          }
        }
      });

      return marketValuesByYear;
    }
  }

  const sumInitialMarketValue = productList.reduce(
    (acc, product) =>
      acc +
      ((product.marketValue ?? 0) +
        (product.ordersInProcessAmount ?? 0) +
        (product.otherValue ?? 0)),
    0
  );

  const yearlyExpectedCashFlow = [];
  data.forEach((yearData, yearIndex) => {
    const productCashFlows = yearData.productCashFlows || [];
    let totalCashFlowForYear = 0;

    productCashFlows.forEach((pcf) => {
      totalCashFlowForYear += pcf.expectedCashFlow?.value ?? 0;
    });

    yearlyExpectedCashFlow[yearIndex] = totalCashFlowForYear;
  });

  const marketValuesByYear = [];
  let aggregatedExpectedMarketValue = sumInitialMarketValue;

  for (let i = 0; i < data.length; i++) {
    if (i < yearlyExpectedCashFlow.length) {
      marketValuesByYear.push(aggregatedExpectedMarketValue);
      aggregatedExpectedMarketValue -= yearlyExpectedCashFlow[i];
    } else {
      marketValuesByYear.push(0);
    }
  }

  data.forEach((item, index) => {
    const coreAllocation = item.coreProductAllocation;
    if (coreAllocation?.selected) {
      for (let i = index; i < marketValuesByYear.length; i++) {
        marketValuesByYear[i] += coreAllocation.value ?? 0;
      }
    }
  });

  return marketValuesByYear;
};

// Replicate getYearlyExpectedCashFlowWithNewCommitments from backoffice chartData.tsx
// Calculates "existing and new" - market values including impact of new allocations
export const getYearlyExpectedCashFlowWithNewCommitments = (
  data,
  assetClassCode,
  products,
  strategies
) => {
  if (!data || data.length === 0) {
    return [];
  }

  const templateCashFlow = data.map(
    (yearData) => yearData.templateCashFlowRate ?? 0
  );

  const cashFlowAllocations = data.map((item) => ({
    year: item.year,
    value: item.cashFlowAllocation?.value ?? 0,
    selected: item.cashFlowAllocation?.selected ?? false,
  }));

  const endYear = data[data.length - 1]?.year;

  const result = [];

  for (let cf of cashFlowAllocations) {
    if (!cf.selected) {
      const existing = result.filter((x) => x.year === cf.year);
      if (existing.length === 0) {
        result.push({ year: cf.year, value: 0, selected: false });
      }
      continue;
    }

    let rateIndex = 0;
    let accumulated = 0;

    for (let year = cf.year; year <= endYear; year++) {
      const existing = result.filter((x) => x.year === year);
      const rate = templateCashFlow[rateIndex] ?? 0;
      accumulated += cf.value * rate;

      if (existing.length > 0) {
        existing[0].value += accumulated;
      } else {
        result.push({ year: year, value: accumulated, selected: true });
      }
      rateIndex++;
    }
  }

  const yearlySumExpectedMarketValues = sumMarketValues(
    data,
    assetClassCode,
    products,
    strategies
  );

  for (let i = 1; i < yearlySumExpectedMarketValues.length; i++) {
    yearlySumExpectedMarketValues[i] -= result[i - 1]?.value ?? 0;
  }

  return yearlySumExpectedMarketValues;
};

// Transform data for stacked bar chart. Returns raw data without formatting.
export const transformDataForStackedChart = (
  data,
  assetClassCode,
  products,
  strategies,
  options = {}
) => {
  const { includeExternal = false, showSimulation = false } = options;

  if (!data || data.length === 0) {
    return {
      mainBars: { data: [] },
      stackedBars: { data: [] },
      simulationBars: { data: [] },
      hasNewCommitments: false,
    };
  }

  const existingValues = sumMarketValues(
    data,
    assetClassCode,
    products,
    strategies
  );
  const existingAndNewValues = getYearlyExpectedCashFlowWithNewCommitments(
    data,
    assetClassCode,
    products,
    strategies
  );

  // Main bar shows "existing" market values (products + core allocations, no new commitments).
  // Illiquid rows carry their market value in `amount`; portfolio rows have none and are recomputed.
  const mainData = data.map((item, index) => {
    const value = item.amount ?? existingValues[index] ?? 0;
    return {
      x: item.year,
      y: value,
      originalItem: item,
      index,
    };
  });

  const externalData = data.map((item, index) => ({
    x: item.year,
    y: item.externalAmount || 0,
    originalItem: item,
    index,
  }));

  const simulationData = data.map((item, index) => {
    const value = existingAndNewValues[index] ?? 0;
    return {
      x: item.year,
      y: value,
      originalItem: item,
      index,
    };
  });

  const hasNewCommitments = data.some(
    (item) => item.cashFlowAllocation?.selected
  );

  // Simulation bars for simulation (existing and new) rendered behind main bars
  const simulationBars = showSimulation
    ? { data: simulationData }
    : { data: [] };

  // Add external data bar if there's external data to show
  const hasExternalData = includeExternal && externalData.some((d) => d.y > 0);
  const hasSimulationBars = simulationBars.data.length > 0;

  return {
    mainBars: {
      roundedTop: !hasExternalData && !hasSimulationBars,
      data: mainData,
    },
    stackedBars: hasExternalData
      ? {
          roundedTop: true,
          data: externalData,
        }
      : { data: [] },
    simulationBars,
    hasNewCommitments,
  };
};
